import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, EnvelopeSimple, InstagramLogo, NavigationArrow } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSeo } from "../hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  useSeo({
    title: "Kontakt & Anfahrt | JS Blond Salon Leingarten bei Heilbronn",
    description: "JS Blond Salon, Daimlerstraße 26, 74211 Leingarten bei Heilbronn. Öffnungszeiten, Anfahrt, Telefon, E-Mail & Online-Terminbuchung. Wir freuen uns auf dich.",
    path: "/kontakt",
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Nachricht gesendet! Wir melden uns bei dir.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="caption block mb-4">Kontakt</span>
            <h1
              className="text-5xl md:text-6xl font-normal tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Sag Hallo
            </h1>
            <p className="text-lg leading-relaxed font-light text-[#2C2C2C]/70">
              Wir freuen uns auf deine Anfrage. Ob Fragen zu unseren Services oder 
              zur Terminbuchung – wir sind gerne für dich da.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-spacing bg-[#F9F7F2]">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Location */}
              <div className="space-y-4">
                <h3
                  className="text-xl font-medium"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Standort
                </h3>
                
                <div className="bg-white p-6 border border-[#E6E2D6]">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-[#9A8C73]" weight="fill" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2C2C] mb-1">JS Blond Salon</p>
                      <p className="text-[#2C2C2C]/70">
                        Daimlerstr. 26<br />
                        74211 Leingarten
                      </p>
                      <a 
                        href="https://www.google.com/maps/search/?api=1&query=Daimlerstraße+26+74211+Leingarten"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#D4AF37] hover:underline mt-2 inline-block"
                      >
                        Route planen
                      </a>
                    </div>
                  </div>
                </div>

                {/* Google Maps Embed */}
                <div className="rounded-lg overflow-hidden border border-[#E6E2D6]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2611.5!2d9.1169!3d49.1486!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDnCsDA4JzU1LjAiTiA5wrAwNycwMS4wiCE!5e0!3m2!1sde!2sde!4v1"
                    width="100%"
                    height="220"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="JS Blond Salon Standort"
                  />
                </div>

                {/* Parking Info */}
                <div className="bg-[#F9F7F2] p-4 rounded-lg">
                  <p className="text-sm text-[#2C2C2C]/70">
                    <strong className="text-[#2C2C2C]">Parken:</strong> 8 eigene Parkplätze direkt am Salon. Weitere kostenlose Parkplätze im Gewerbegebiet. Sehr gute Autobahnanbindung.
                  </p>
                </div>
              </div>

              {/* Hours & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-6 border border-[#E6E2D6]">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-[#9A8C73]" weight="light" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2C2C]">Öffnungszeiten</p>
                      <p className="text-sm text-[#2C2C2C]/70">
                        Termine nach Vereinbarung<br />
                        24h online buchbar
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 border border-[#E6E2D6]">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <EnvelopeSimple size={20} className="text-[#9A8C73]" weight="light" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2C2C]">Email</p>
                      <a 
                        href="mailto:info@js-blondsalon.de" 
                        className="text-sm text-[#9A8C73] hover:underline"
                        data-testid="contact-email-link"
                      >
                        info@js-blondsalon.de
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3
                  className="text-lg font-medium mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Folge uns
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.a
                    href="https://www.instagram.com/jsblondsalon"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-salon"
                    className="flex items-center gap-2 bg-white px-5 py-3 border border-[#E6E2D6] hover:bg-[#9A8C73] hover:text-white hover:border-[#9A8C73] transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <InstagramLogo size={20} weight="fill" />
                    <span className="text-sm">@jsblondsalon</span>
                  </motion.a>
                  <motion.a
                    href="https://www.instagram.com/jennifervallentin"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-jennifer"
                    className="flex items-center gap-2 bg-white px-5 py-3 border border-[#E6E2D6] hover:bg-[#9A8C73] hover:text-white hover:border-[#9A8C73] transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                  >
                    <InstagramLogo size={20} weight="fill" />
                    <span className="text-sm">@jennifervallentin</span>
                  </motion.a>
                </div>
              </div>

              {/* Book Button */}
              <motion.a
                href="https://www.phorest.com/salon/jsblondsalonughaftungsbeschrae"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-book-btn"
                className="btn-primary inline-block text-center"
                whileHover={{ scale: 1.02 }}
              >
                Termin online buchen
              </motion.a>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="bg-white p-8 lg:p-10 border border-[#E6E2D6]">
                <h3
                  className="text-2xl font-medium mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Nachricht senden
                </h3>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                        Vorname
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                        className="w-full px-4 py-3 bg-[#F9F7F2] border border-[#E6E2D6] focus:border-[#9A8C73] focus:outline-none transition-colors"
                        placeholder="Dein Vorname"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                        Nachname
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        className="w-full px-4 py-3 bg-[#F9F7F2] border border-[#E6E2D6] focus:border-[#9A8C73] focus:outline-none transition-colors"
                        placeholder="Dein Nachname"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 bg-[#F9F7F2] border border-[#E6E2D6] focus:border-[#9A8C73] focus:outline-none transition-colors"
                      placeholder="deine@email.de"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone-input"
                      className="w-full px-4 py-3 bg-[#F9F7F2] border border-[#E6E2D6] focus:border-[#9A8C73] focus:outline-none transition-colors"
                      placeholder="+49 ..."
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Nachricht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-message-input"
                      className="w-full px-4 py-3 bg-[#F9F7F2] border border-[#E6E2D6] focus:border-[#9A8C73] focus:outline-none transition-colors resize-none"
                      placeholder="Deine Nachricht..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  >
                    {isSubmitting ? "Wird gesendet..." : "Senden"}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
